import * as Sentry from '@sentry/react';

/**
 * Sentry error monitoring + performance tracing.
 *
 * Everything is gated behind REACT_APP_SENTRY_DSN: when the DSN is absent
 * (e.g. local development without a DSN configured) `initSentry` is a no-op,
 * so the SDK stays dormant and adds no runtime overhead.
 *
 * Relevant env vars:
 *   REACT_APP_SENTRY_DSN                  - Sentry project DSN. Required to enable.
 *   REACT_APP_SENTRY_ENVIRONMENT          - environment tag (falls back to REACT_APP_SETTING_FLAG).
 *   REACT_APP_SENTRY_TRACES_SAMPLE_RATE   - performance tracing sample rate 0..1 (default 0.1).
 *   REACT_APP_SENTRY_RELEASE              - release id; CI sets this to the commit SHA (github.sha)
 *                                           so every error traces back to its exact commit/deploy.
 *                                           MUST match the release name created by sentry-cli in CI.
 *   REACT_APP_RELEASE_NAME / REACT_APP_BRANCH_NAME - legacy release fallbacks.
 */
export function initSentry(): void {
  const dsn = process.env.REACT_APP_SENTRY_DSN;

  if (!dsn) {
    return;
  }

  const environment =
    process.env.REACT_APP_SENTRY_ENVIRONMENT ||
    process.env.REACT_APP_SETTING_FLAG ||
    'development';

  const parsedTracesRate = Number(
    process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE
  );
  const tracesSampleRate = Number.isFinite(parsedTracesRate)
    ? parsedTracesRate
    : 0.1;

  // Release identifier. CI injects the commit SHA (github.sha) via
  // REACT_APP_SENTRY_RELEASE and creates a matching release with sentry-cli,
  // so each event links back to its exact commit and deploy. Fall back to the
  // pre-existing release/branch vars for environments that don't set it yet.
  const release =
    process.env.REACT_APP_SENTRY_RELEASE ||
    process.env.REACT_APP_RELEASE_NAME ||
    process.env.REACT_APP_BRANCH_NAME ||
    undefined;

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [Sentry.browserTracingIntegration()],
    // Performance tracing sample rate.
    tracesSampleRate,
    // Only trace requests to our own API to avoid noisy CORS preflights.
    tracePropagationTargets: [
      /^\//,
      ...(process.env.REACT_APP_API_URL ? [process.env.REACT_APP_API_URL] : []),
    ],
  });
}
